import { useEffect, useState, type FormEvent } from 'react'
import { Notice } from '@shared/ui/Notice'
import {
  getPinStatus,
  removePin,
  setupPin,
  type PinStatus,
} from '@features/auth/services/pinVaultService'
import { isValidPin, PIN_MAX_LENGTH, PIN_MIN_LENGTH } from '@/crypto/pin-vault'
import { Icon } from '@/icons/Icon'
import { useTranslation } from '@/i18n'

/**
 * PIN ロック解除 設定セクション (TASK-WEB-PIN)。
 *
 *  - 未設定: 4-6 桁の PIN + 確認入力 → setupPin で IndexedDB に wrap-SK 保存
 *  - 設定済: 削除 button + 警告 Notice
 *  - sessionStore が UNLOCKED でないと setup 不可 (services 側でガード、ここは UX)
 *
 * inline-展開 (SettingsPage の security card 配下) — modal portal は使わない。
 */
export const PinUnlockSection = (): JSX.Element => {
  const { t } = useTranslation()
  const [status, setStatus] = useState<PinStatus | null>(null)
  const [pin, setPin] = useState('')
  const [confirm, setConfirm] = useState('')
  const [showPin, setShowPin] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [busy, setBusy] = useState(false)
  const [errorKey, setErrorKey] = useState<string | null>(null)
  const [successKey, setSuccessKey] = useState<string | null>(null)

  const refreshStatus = async (): Promise<void> => {
    const s = await getPinStatus()

    setStatus(s)
  }

  useEffect(() => {
    void refreshStatus()
  }, [])

  const onlyDigits = (s: string): string => s.replace(/\D/g, '').slice(0, PIN_MAX_LENGTH)

  // ライブ検証フラグ — submit 前から赤字で即時表示する。
  //   - 入力が始まっていて (length>0) 最小桁数未満 → 「4–6 桁の数字を入力してください」
  //   - 確認入力が完了 (>=4) で PIN と不一致 → 「PIN が一致しません」
  //     (review I-9: 以前は `pin.length >= PIN_MIN_LENGTH` ゲートを足していたため、
  //     ユーザーが確認 input を先に貼付 → PIN を後から入力 する操作で mismatch
  //     エラーが表示されない silent disabled submit 問題があった)
  const pinTooShort = pin.length > 0 && pin.length < PIN_MIN_LENGTH
  const confirmTooShort = confirm.length > 0 && confirm.length < PIN_MIN_LENGTH

  const confirmMismatch = confirm.length >= PIN_MIN_LENGTH && pin !== confirm

  const onSetup = async (ev: FormEvent<HTMLFormElement>): Promise<void> => {
    ev.preventDefault()
    setErrorKey(null)
    setSuccessKey(null)

    if (!isValidPin(pin)) {
      setErrorKey('settings.security.pin_error_format')

      return
    }
    if (pin !== confirm) {
      setErrorKey('settings.security.pin_error_mismatch')

      return
    }

    setBusy(true)
    try {
      await setupPin(pin)
      setPin('')
      setConfirm('')
      setSuccessKey('settings.security.pin_setup_success')
      await refreshStatus()
    } catch {
      setErrorKey('settings.security.pin_error_setup')
    } finally {
      setBusy(false)
    }
  }

  const onRemove = async (): Promise<void> => {
    setBusy(true)
    setErrorKey(null)
    try {
      await removePin()
      setSuccessKey('settings.security.pin_remove_success')
      await refreshStatus()
    } catch {
      setErrorKey('settings.security.pin_error_remove')
    } finally {
      setBusy(false)
    }
  }

  if (status === null) {
    return (
      <div className="card settings-security-item">
        <div className="setting-row-sub">{t('common.ui.loading')}</div>
      </div>
    )
  }

  if (status.configured) {
    return (
      <div className="card settings-security-item" data-testid="pin-section-enabled">
        <Notice tone="info" ico="check">
          {t('settings.security.pin_enabled_body')}
        </Notice>
        {successKey !== null && (
          <Notice tone="info" ico="check">
            {t(successKey)}
          </Notice>
        )}
        {errorKey !== null && (
          <Notice tone="danger" ico="alert">
            {t(errorKey)}
          </Notice>
        )}
        <div className="card-row-flex">
          <div className="settings-item-sub">
            {t('settings.security.pin_attempts_remaining', {
              n: String(status.attemptsLeft),
            })}
          </div>
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => void onRemove()}
            disabled={busy}
            data-testid="pin-remove-btn"
          >
            {t('settings.security.pin_remove_action')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <form
      className="card settings-security-item"
      onSubmit={ev => void onSetup(ev)}
      data-testid="pin-section-setup-form"
    >
      <div className="settings-item-sub">{t('settings.security.pin_setup_body')}</div>

      {successKey !== null && (
        <Notice tone="info" ico="check">
          {t(successKey)}
        </Notice>
      )}
      {errorKey !== null && (
        <Notice tone="danger" ico="alert">
          {t(errorKey)}
        </Notice>
      )}

      <div className="field">
        <label className="field-label" htmlFor="pin-setup">
          {t('settings.security.pin_label')}
        </label>
        <div className="input-group">
          <input
            id="pin-setup"
            type={showPin ? 'text' : 'password'}
            className="input"
            value={pin}
            onChange={ev => setPin(onlyDigits(ev.target.value))}
            placeholder={t('settings.security.pin_placeholder')}
            inputMode="numeric"
            autoComplete="off"
            minLength={PIN_MIN_LENGTH}
            maxLength={PIN_MAX_LENGTH}
            pattern="[0-9]{4,6}"
            required
            aria-invalid={pinTooShort}
            aria-describedby={pinTooShort ? 'pin-setup-error' : undefined}
          />
          <div className="input-suffix">
            <button
              type="button"
              className="icon-btn sm"
              onClick={() => setShowPin(v => !v)}
              aria-label={showPin ? t('common.password.hide') : t('common.password.show')}
              aria-pressed={showPin}
              tabIndex={-1}
            >
              <Icon name={showPin ? 'eye-off' : 'eye'} aria-hidden="true" />
            </button>
          </div>
        </div>
        {pinTooShort && (
          <p className="field-error" id="pin-setup-error" role="alert">
            {t('settings.security.pin_error_format')}
          </p>
        )}
      </div>

      <div className="field">
        <label className="field-label" htmlFor="pin-confirm">
          {t('settings.security.pin_confirm_label')}
        </label>
        <div className="input-group">
          <input
            id="pin-confirm"
            type={showConfirm ? 'text' : 'password'}
            className="input"
            value={confirm}
            onChange={ev => setConfirm(onlyDigits(ev.target.value))}
            placeholder={t('settings.security.pin_placeholder')}
            inputMode="numeric"
            autoComplete="off"
            minLength={PIN_MIN_LENGTH}
            maxLength={PIN_MAX_LENGTH}
            pattern="[0-9]{4,6}"
            required
            aria-invalid={confirmTooShort || confirmMismatch}
            aria-describedby={confirmTooShort || confirmMismatch ? 'pin-confirm-error' : undefined}
          />
          <div className="input-suffix">
            <button
              type="button"
              className="icon-btn sm"
              onClick={() => setShowConfirm(v => !v)}
              aria-label={showConfirm ? t('common.password.hide') : t('common.password.show')}
              aria-pressed={showConfirm}
              tabIndex={-1}
            >
              <Icon name={showConfirm ? 'eye-off' : 'eye'} aria-hidden="true" />
            </button>
          </div>
        </div>
        {confirmTooShort && (
          <p className="field-error" id="pin-confirm-error" role="alert">
            {t('settings.security.pin_error_format')}
          </p>
        )}
        {!confirmTooShort && confirmMismatch && (
          <p className="field-error" id="pin-confirm-error" role="alert">
            {t('settings.security.pin_error_mismatch')}
          </p>
        )}
      </div>

      <div className="card-row-flex">
        <div className="settings-item-sub">{t('settings.security.pin_max_attempts_hint')}</div>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={busy || !isValidPin(pin) || pin !== confirm}
          data-testid="pin-setup-submit"
        >
          {t('settings.security.pin_setup_action')}
        </button>
      </div>
    </form>
  )
}
